import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import { placementZodSchema } from "@@/shared/relative-placement";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { storageItemImageReferenceZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-reference";
import type { HexColor } from "@@/shared/stylable/hex-color/hex-color";
import { hexColor } from "@@/shared/stylable/hex-color/hex-color";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    asTranslatableValue,
    translatableZodSchema,
    translation,
} from "@@/shared/translations/translation";
import { faHeading } from "@fortawesome/pro-solid-svg-icons";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    HzAlignment} from "../../../alignments-and-placements";
import {
    alignment,
    hzAlignmentZodSchema,
} from "../../../alignments-and-placements";
import { pageBlockBaseZodSchema } from "../../page-block-base";
import { pageBlockIdFactory } from "../../page-block-id";
import type {
    PageBlockTitleSize} from "../../shared/page-block-title-size";
import {
    pageBlockTitleSizeZodSchema,
} from "../../shared/page-block-title-size";

const headerPageBlockZodSchema_V1 = pageBlockBaseZodSchema.merge(
    z.object({
        title: translatableZodSchema,
        subtitle: translatableZodSchema,
        alignment: hzAlignmentZodSchema,
        subtitlePlacement: placementZodSchema,
        type: z.literal("HEADER_PAGE_BLOCK"),
        titleSize: pageBlockTitleSizeZodSchema,
        textShadow: z.boolean(),
        backgroundImage: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
    }),
);

type HeaderPageBlock_V1 = z.infer<typeof headerPageBlockZodSchema_V1>;

const headerPageBlockIcon_V1 = faHeading;
type HeaderPageBlockParams = Except<
    HeaderPageBlock_V1,
    | "_id"
    | "type"
    | "_type"
    | "_createdAt"
    | "title"
    | "subtitle"
    | "titleSize"
    | "textShadow"
    | "alignment"
> & {
    title?: Translatable;
    subtitle?: Translatable;
    textShadow?: boolean;
    titleSize?: PageBlockTitleSize;
    alignment?: HzAlignment;
};
const headerPageBlockFactory_V1 = (
    params: HeaderPageBlockParams,
): HeaderPageBlock_V1 => {
    const horizontalAlignment = params.alignment ?? alignment.auto;
    return {
        alignment: horizontalAlignment,
        subtitlePlacement: params.subtitlePlacement ?? "below",
        textColor: params.textColor ?? ("#333333" as HexColor),
        backgroundColor: params.backgroundColor ?? hexColor("#ffffff"),
        subtitle: asTranslatableValue(params.subtitle),
        title: asTranslatableValue(
            params.title || translation({ sv: "Rubrik", en: "Header" }),
        ),
        pageId: params.pageId,
        backgroundImage: params.backgroundImage,
        _id: pageBlockIdFactory(),
        _type: "PAGE_BLOCK",
        _createdAt: nowIsoAndUnix(),
        type: "HEADER_PAGE_BLOCK",
        accentColor: params.accentColor,
        titleSize: params.titleSize ?? "M",
        textShadow: false,
    };
};

const HeaderPageBlock_Edit_V1 = () => {
    return null;
};

const headerPageBlockType_V1 = "HEADER_PAGE_BLOCK";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isHeaderPageBlock_V1 = (input: any): input is HeaderPageBlock_V1 => {
    return input._type === "PAGE_BLOCK" && input.type === "HEADER_PAGE_BLOCK";
};

export {
    HeaderPageBlock_Edit_V1,
    headerPageBlockFactory_V1,
    headerPageBlockIcon_V1,
    headerPageBlockType_V1,
    headerPageBlockZodSchema_V1,
    isHeaderPageBlock_V1,
};
export type { HeaderPageBlock_V1 };
